// Canonical roles module — CONTRACTS.md §7 (owned here; every consumer imports from `@/auth/roles`).
// `AppRole` is a const object + derived type, so both `AppRole.Administrator` (value) and `AppRole` (type) work.
export const AppRole = {
  Administrator: 'administrator',
  SystemAdmin: 'system_admin',
  ContentManager: 'content_manager',
  Finance: 'finance',
  Marketer: 'marketer',
  Operator: 'operator',
} as const;

export type AppRole = (typeof AppRole)[keyof typeof AppRole]; // 'administrator' | ...

export const ALL_ROLES: AppRole[] = Object.values(AppRole);

// Colors ported from RoleUtils.kt / AppRole.getTheme(); the badgeClass styles resolve to these — not for gating.
export const ROLE_META: Record<AppRole, { label: string; badgeClass: string }> = {
  administrator: { label: 'Administrator', badgeClass: 'badge-role-administrator' },
  system_admin: { label: 'System Admin', badgeClass: 'badge-role-system-admin' },
  content_manager: { label: 'Content Manager', badgeClass: 'badge-role-content-manager' },
  finance: { label: 'Finance', badgeClass: 'badge-role-finance' },
  marketer: { label: 'Marketer', badgeClass: 'badge-role-marketer' },
  operator: { label: 'Support Agent', badgeClass: 'badge-role-operator' },
};

// Cosmetic badge palette ported from Role.kt getTheme() — not for gating.
export const ROLE_BADGE_COLOR: Record<AppRole, string> = {
  administrator: '#0A84FF',
  system_admin: '#8B5CF6',
  content_manager: '#10B981',
  finance: '#EF4444',
  marketer: '#F59E0B',
  operator: '#06B6D4',
};

// The SSO callback sends DISPLAY NAMES ("Administrator", "System Admin"); the JWT security claim sends the
// `ROLE_<DisplayName>` form ("ROLE_System Admin"). Accept code value OR display name OR `ROLE_`-prefixed claim,
// case-insensitive -> canonical AppRole (CONTRACTS.md §7).
const BY_LABEL = new Map<string, AppRole>(
  Object.entries(ROLE_META).map(([code, m]) => [m.label.toLowerCase(), code as AppRole]),
);

/** Legacy Entra / Confluence aliases → canonical AppRole (e.g. Operator → Support Agent). */
const LEGACY_LABELS = new Map<string, AppRole>([
  ['operator', AppRole.Operator], // already covered by code value; kept for clarity
  ['help & support', AppRole.Operator],
]);

export function normalizeRole(raw: string | null | undefined): AppRole | null {
  if (!raw) return null;
  const stripped = raw.trim().replace(/^ROLE_/i, ''); // 'ROLE_System Admin' -> 'System Admin'
  const s = stripped.toLowerCase();
  if ((ALL_ROLES as string[]).includes(s)) return s as AppRole; // code value ('system_admin')
  return BY_LABEL.get(s) ?? LEGACY_LABELS.get(s) ?? null; // display name ('system admin')
}

export function parseRolesParam(rolesParam: string | null): AppRole[] {
  if (!rolesParam) return [];
  return rolesParam
    .split(',')
    .map((r) => normalizeRole(r))
    .filter((r): r is AppRole => r !== null);
}
